import { type FieldSpec, isPickerValue } from './field-spec';
import { kqlStringLiteral } from './kql-literal';
import type { Answer, Selected } from './page';
import { selectedFrom } from './page';
import type { AzureResource } from './types';

/** The `ui:options` this picker offers: exactly three keys. */
export interface ResourcePickerUiOptions {
  resourceGroupRef?: string;
  resourceType?: string;
  subscriptionRef?: string;
}

/** The fields of {@link AzureResource}, one per column the tail below projects. */
export const RESOURCE_FIELDS = {
  id: 'string',
  location: 'string',
  name: 'string',
  properties: 'present',
  resourceGroup: 'string',
  subscriptionId: 'string',
  type: 'string',
} as const satisfies FieldSpec<AzureResource>;

const RESOURCE_TAIL = [
  '| project id, name, type, location, resourceGroup, subscriptionId, properties',
  '| order by tolower(name) asc // on the visible text, case-insensitive',
].join('\n');

/**
 * Builds the KQL for the resource picker.
 *
 * @remarks
 * Both comparisons use `=~` and not `==`, and that is not taste. Resource Graph
 * normalizes `type` and `resourceGroup` to lower case, so a `==` against the
 * author's spelling returns zero rows, without an error and without a hint.
 * Measured: `== "RG10DEVELOPERPORTALBASE100-PROD"` gave 0 rows, `=~` gave 21
 *.
 *
 * The subscription travels as the `subscriptions` argument, not in this text
 *.
 *
 * @param input - The chosen resource group and the author's `resourceType`.
 * @returns The full query text.
 */
export function buildResourceQuery(input: {
  resourceGroup: string;
  resourceType: string;
}): string {
  const groupLiteral = kqlStringLiteral(input.resourceGroup);
  const typeLiteral = kqlStringLiteral(input.resourceType);

  return [
    'resources',
    `| where resourceGroup =~ ${groupLiteral}`,
    `| where type =~ ${typeLiteral}`,
    RESOURCE_TAIL,
  ].join('\n');
}

/**
 * Whether the picker may fetch.
 *
 * @remarks
 * Both referenced fields must be filled. A chain of three falls over by itself
 * as long as every link follows its own rule: a switched subscription clears
 * the group, and an empty group makes this picker wait.
 */
export function resourceQueryEnabled(input: {
  resourceGroup: string | undefined;
  subscriptionId: string | undefined;
}): boolean {
  const { resourceGroup, subscriptionId } = input;
  return (
    subscriptionId !== undefined &&
    subscriptionId.length > 0 &&
    resourceGroup !== undefined &&
    resourceGroup.length > 0
  );
}

/** Narrows an already-validated answer to what this picker guarantees. */
export function selectResources(answer: Answer): Selected<AzureResource> {
  return selectedFrom(answer, isAzureResource);
}

function isAzureResource(row: unknown): row is AzureResource {
  return isPickerValue<AzureResource>(row, RESOURCE_FIELDS);
}

/**
 * Whether a resource type has the shape every Azure type has.
 *
 * @remarks
 * One slash with something on either side. Whether that namespace exists is
 * Azure's answer to give, and an empty list naming the type is how the picker
 * surfaces a typo that has the right shape.
 */
export function isResourceTypeShaped(written: string): boolean {
  // `split` always yields at least one element, but an indexed read is typed
  // as possibly absent, so both halves are read the same defensive way.
  const [namespace, ...rest] = written.split('/');
  return (
    rest.length === 1 &&
    (namespace?.length ?? 0) > 0 &&
    (rest[0]?.length ?? 0) > 0
  );
}

/**
 * A resource type that is not `<namespace>/<type>`.
 *
 * @remarks
 * A type always carries a slash, and that is knowable without asking Azure, so
 * a typo is caught before anything is fetched.
 */
export function describeMalformedResourceType(written: string): string {
  return `"${written}" is not a resource type. A type is written as <namespace>/<type>, for example Microsoft.Network/routeTables.`;
}
